import React from 'react';

import {Box} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useHistory} from 'react-router-dom';

import {AddressServiceRequest, EfeAccordionValues, EfeResponse, Field, View} from 'commons/models';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {EfeAccordion} from 'components';
import {MainContent} from 'layouts/commons';
import {useFlowFieldSearch, useSpinner} from 'hooks';
import {nextStep} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {ClientService} from 'services';
import RealOwnerLocationInfoForm from './real-owner-location-info-form/RealOwnerLocationInfoForm';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {fieldCatalogGetter} from 'hooks/FlowHook';

function RealOwnerLocationInfo() {
    const history = useHistory();
    const {flow, updateModuleStep} = useFlowContext();
    const [viewFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const [fields, setFields] = React.useState<Array<Field>>([]);
    const {menu, kycId, expedientId, fatherKycId} = useMenuContext();
    const {tempRealOwner, editLocation, setEditLocation, setTempRealOwner, setTempRealOwnerActive} =
        useRelatedPeopleContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const {enqueueSnackbar} = useSnackbar();
    const [auxRealownerInfo, setAuxRealOwnerInfo] = React.useState<any>();

    React.useEffect(() => {
        if (!fields || (fields && fields.length <= 0)) {
            setFields(viewFields);
        }
    }, []);

    React.useEffect(() => {
        if (fatherKycId > 0 && kycId > 0 && editLocation) {
            (async function () {
                try {
                    setIsLoading(true);
                    const response: EfeResponse<AddressServiceRequest> =
                        await ClientService.getRealOwnerAddress(kycId);
                    if (response && response.data) {
                        setAuxRealOwnerInfo(response.data);
                    }
                    setIsLoading(false);
                } catch (error) {
                    setIsLoading(false);
                    setAuxRealOwnerInfo(undefined);
                }
            })();
        }
    }, [editLocation]);

    async function overrideFields(view: View | undefined) {
        if (view && view.fields) {
            await setFields([...view.fields]);
        } else {
            await setFields([...viewFields]);
        }
    }

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Direcci\u00F3n del Propietario Real',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RealOwnerLocationInfoForm
                            viewId={flow.data.viewId}
                            submitHandler={onSubmit}
                            viewKey={flow.data.id}
                            foreignCitizen={tempRealOwner?.foreignCitizen || false}
                            fieldHandler={overrideFields}
                            auxRealownerInfo={auxRealownerInfo}
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.REAL_OWNER_LOCATION_INFO === flow.data.expandKey,
        },
    ];

    async function onSubmit(values: AddressServiceRequest) {
        setIsLoading(true);
        const request = {...values};
        request.kycId = kycId;
        request.viewId = flow.data.viewId;
        request.expedientId = expedientId;
        request.viewFatherId = flow.data.fatherId;
        try {
            const response: EfeResponse<AddressServiceRequest> = await ClientService.saveAddress(
                request,
                tempRealOwner?.foreignCitizen || false
            );
            if (response && response.data) {
                enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                    variant: SnackbarVariants.SUCCESS,
                });
                setFields([...fields]);
                const path = nextStep(flow, menu);
                if (tempRealOwner) {
                    setTempRealOwnerActive(false);
                    setTempRealOwner(undefined);
                }
                if (Constants.MODULE_STEP !== path) {
                    setIsLoading(false);
                    history.push(path);
                } else {
                    updateModuleStep(true);
                    flow.data.expandKey = path;
                    setIsLoading(false);
                }
            } else {
                setFields([...fields]);
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }

    function onChangeHandler() {
        setTempRealOwnerActive(true);
        setEditLocation(true);
        history.push(HistoryRoutes.REAL_OWNER_ADDRESS);
    }

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}

            <EfeAccordion onChange={onChangeHandler} values={accordion} />
        </MainContent>
    );
}

export default RealOwnerLocationInfo;
